import { broker } from '#~/agent/index.js';
import { fetchAgentsForRoom } from '#~/api/agent/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { useDepot, useProducer } from '#~/depot/hooks.js';
import { StudioError } from '#~/error/index.js';
import { broker as lkbroker } from '#~/livekit/index.js';
import { Context } from './context.js';
import { Signal } from './signal.jsx';
import type { Props } from './types.js';
import { useRoomAgentStatus } from './utils.js';
import { Watchdog } from './watchdog.jsx';
import { Loader } from '@studio/design-system/loader';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { unwrap } from '@studio/js-utils/async';
import { toStatus } from '@studio/models/agent';
import type { Status } from '@studio/models/agent';
import { useAsyncEffect } from '@studio/react-utils/hooks';
import { AgentRunningState } from '@tryiris-ai/common.protojs/api/v1/agent_direct';
import { noop } from 'lodash-es';
import { type ReactElement } from 'react';

/** Agent `<Provider>`. */
export const Provider = ({ children, roomId, noWatchdog }: Props): ReactElement => {
  const { getAccessTokenSilently } = useAuth0();
  const producer = useProducer();

  const { isReady: isAgentReady, error } = useRoomAgentStatus(roomId);
  // Cached agent status can outlive the runtime room removed during a route handoff.
  const isRoomReady = useDepot((state) => state.rooms[roomId] !== undefined);
  const isReady = isAgentReady && isRoomReady;

  const livekit = lkbroker.acquire(roomId);
  // @llm do the same thing here but use the onAgentPresence event
  useAsyncEffect(
    function* generator(_signal: AbortSignal) {
      if (!isReady) {
        return noop;
      }

      // Look up agent details.
      const agents = yield* unwrap(
        fetchAgentsForRoom(roomId, yield* unwrap(getAccessTokenSilently()))
      );

      // Probe-ordering guard (race fix 2026-08-25): presence events overlap —
      // each callback awaits a ping (up to 10s) and used to write status in
      // COMPLETION order, so a stale probe could overwrite a newer verdict
      // last (bridge shown offline-then-online out of order, or "connected"
      // after a real disconnect). Only the newest event's probe may write.
      const presenceEpochs = new Map<string, number>();

      const unsub = livekit?.onAgentPresence(async (agentId: string, _inRoom: boolean) => {
        const agent = agents[agentId as keyof typeof agents];

        if (!agent) {
          return;
        }

        const proxy = broker.acquire(agent.id);

        void proxy?.emit('state.stopped');

        // Interface-switch incident 2026-08-25: probe before offline. During
        // a network change both livekit rooms can briefly report the agent
        // absent (cloud ws re-establishing, local room resuming) while the
        // bridge itself is healthy — flipping to 'offline' on inRoom alone
        // flapped the UI for 10+ seconds. Presence now means REACHABILITY:
        // ping rides the state channel (hybrid local+cloud), so it succeeds
        // through either surviving route and only fails when the bridge is
        // genuinely gone. The ping's own timeout (10s) is the deliberate
        // defer-before-offline window; a true shutdown still flips offline
        // after it.
        const epoch = (presenceEpochs.get(agentId) ?? 0) + 1;
        presenceEpochs.set(agentId, epoch);

        const canPing = await proxy?.ping();

        if (presenceEpochs.get(agentId) !== epoch) {
          // A newer presence event started its own probe while this one was
          // in flight — its verdict owns the status; discard this one.
          return;
        }

        producer(({ agents }) => {
          const a = agents[agent.id];

          if (a) {
            const prevStatus = a.status.slice() as Status;

            // Reachable = online, whether or not the presence snapshot has
            // caught up (see probe-before-offline comment above).
            a.status = toStatus(
              canPing
                ? AgentRunningState.AGENT_RUNNING_STATE_START
                : AgentRunningState.AGENT_RUNNING_STATE_STOP,
              !!a.embedded
            );

            const changed = prevStatus !== a.status;

            if (!changed) {
              return;
            }

            if (a.status === 'offline') {
              a.lastShutDownAt = new Date();
            }

            if (a.status === 'online' || a.status === 'idle' || a.status === 'booting') {
              void proxy?.emit('state.hardReset');
            }
          }
        });
      });

      return () => {
        unsub?.();
        lkbroker.destroy(roomId);
      };
    },
    [roomId, isReady]
  );

  if (error) {
    throw new StudioError(`Unable to obtain ${vendorName()} Bridge details.`, { cause: error });
  }

  return !isReady ? (
    <Loader />
  ) : (
    <Context.Provider value={roomId}>
      {children}
      <Signal />
      {!noWatchdog && <Watchdog />}
    </Context.Provider>
  );
};
